/**
 * Buyer "Logistics RFQs sent" — friendly copy only (no internal status codes in UI).
 * Stage keys stay internal for API payloads only.
 */

/** True if a stage cell is finished (legacy string or `{ status: "completed" }`). */
export function isLogisticsStageProgressDone(cell) {
  if (cell == null) return false;
  if (typeof cell === "object" && !Array.isArray(cell)) {
    const st = String(cell.status ?? "")
      .trim()
      .toLowerCase();
    return st === "completed" || st === "complete" || st === "done";
  }
  const s = String(cell).trim().toLowerCase();
  return s === "done" || s === "completed" || s === "complete" || s === "true" || s === "1" || s === "yes";
}

export const DELIVERY_STAGE_CHECKBOXES = [
  { key: "eway_generate", label: "E-Way Bill Generation" },
  { key: "eway_track", label: "E-Way Bill Tracking" },
  { key: "manifest_create", label: "Create Shipping Manifest" },
  { key: "manifest_accept", label: "Accept Shipping Manifest" },
  { key: "transit", label: "Transit" },
  { key: "grn_generate", label: "Generate GRN" },
  { key: "grn_accept", label: "Accept GRN" },
];

const STAGE_HEADLINE = {
  eway_generate: "E-Way pending",
  eway_track: "E-Way tracking",
  manifest_create: "Shipping manifest",
  manifest_accept: "Manifest confirmation",
  transit: "In transit",
  grn_generate: "GRN pending",
  grn_accept: "GRN confirmation",
};

/**
 * Buyer may accept once a manifest exists and accept step is in the plan.
 * Do not require `currentStageKey === manifest_accept` — pointer often lags after provider creates manifest.
 */
export function canOfferManifestAccept(row) {
  const shippingManifestId = row.shippingManifestId != null ? Number(row.shippingManifestId) : null;
  const hasManifest = Number.isFinite(shippingManifestId) && shippingManifestId > 0;
  if (!hasManifest) return false;
  if (row.shippingManifestAccepted) return false;

  const process = row.deliveryProcess;
  const manifestAcceptDone = isLogisticsStageProgressDone(process?.stageProgress?.manifest_accept);
  if (manifestAcceptDone) return false;

  const selected = Array.isArray(process?.selectedStages) ? process.selectedStages : [];
  if (selected.length > 0 && !selected.includes("manifest_accept")) return false;

  return true;
}

function pickupLower(row) {
  return String(row.handoverPickupStatus ?? "").trim().toLowerCase();
}

function allStagesDone(process) {
  if (!process?.selectedStages?.length) return false;
  const prog = process.stageProgress || {};
  return process.selectedStages.every((k) => isLogisticsStageProgressDone(prog[k]));
}

/**
 * @param {object} row normalized list row (includes handoverPickupStatus, deliveryProcess, acceptedQuoteId, handoverId, quoteCount)
 */
export function getLogisticsRfqSentCardUi(row) {
  const hasProvider =
    row.logisticsProviderCompanyId != null &&
    Number.isFinite(Number(row.logisticsProviderCompanyId)) &&
    Number(row.logisticsProviderCompanyId) > 0;

  if (!hasProvider) {
    return {
      primaryLine: 'Submitted — pending provider',
      secondaryLine: 'No logistics provider assigned yet.',
      showSelectStages: false,
      showAdvanceStage: false,
      progressPercent: null,
    };
  }

  const pickup = pickupLower(row);
  const hasAccepted = row.acceptedQuoteId != null && Number(row.acceptedQuoteId) > 0;
  const hasHandover = row.handoverId != null && Number(row.handoverId) > 0;
  const process = row.deliveryProcess;

  if (!hasAccepted) {
    return {
      primaryLine: "Awaiting your quote decision",
      secondaryLine: null,
      showSelectStages: false,
      showAdvanceStage: false,
      progressPercent: null,
    };
  }

  if (!hasHandover) {
    return {
      primaryLine: "Handover required",
      secondaryLine: "Complete handover details for your accepted quote.",
      showSelectStages: false,
      showAdvanceStage: false,
      progressPercent: null,
    };
  }

  if (pickup !== "picked_up") {
    return {
      primaryLine: "Handover completed",
      secondaryLine: "Awaiting pickup by your logistics provider.",
      showSelectStages: false,
      showAdvanceStage: false,
      progressPercent: null,
    };
  }

  // No delivery_process row yet → buyer has not made a decision after pickup.
  if (!process) {
    return {
      primaryLine: "Pickup completed",
      secondaryLine: "Choose the next steps for this shipment.",
      showSelectStages: true,
      showAdvanceStage: false,
      progressPercent: 0,
    };
  }

  // delivery_process row exists but with NO stages → buyer explicitly chose
  // "Skip & continue to invoice". Hide the stage controls and surface the
  // skip state; the invoice action will appear via its own gate.
  if (!process.selectedStages?.length) {
    return {
      primaryLine: "Stages skipped",
      secondaryLine: "Provider can now generate the invoice for this shipment.",
      showSelectStages: false,
      showAdvanceStage: false,
      progressPercent: 100,
    };
  }

  if (allStagesDone(process)) {
    return {
      primaryLine: "Completed",
      secondaryLine: "All selected delivery steps are finished.",
      showSelectStages: false,
      showAdvanceStage: false,
      progressPercent: 100,
    };
  }

  const cur = process.currentStageKey;
  const headline = cur && STAGE_HEADLINE[cur] ? STAGE_HEADLINE[cur] : "In progress";
  const total = process.selectedStages.length;
  let doneCount = 0;
  for (const k of process.selectedStages) {
    if (isLogisticsStageProgressDone(process.stageProgress?.[k])) doneCount += 1;
  }
  const progressPercent = total > 0 ? Math.round((doneCount / total) * 100) : null;

  return {
    primaryLine: headline,
    secondaryLine: "Follow each step in order until the shipment is complete.",
    showSelectStages: false,
    showAdvanceStage: Boolean(cur),
    progressPercent,
  };
}
